import { useState, useEffect, useCallback, useRef } from "react";
import { Save, User, AlertCircle, Eye, EyeOff, Loader2, Shield, Package, Receipt, CreditCardIcon, ShoppingBag, ChevronDown, ChevronUp, CheckCircle, Download, RefreshCw } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import authService from "../../services/authService";
import orderService from "../../services/orderService";
import productService from "../../services/productService";
import polarService from "../../services/polarService";
import Toast from "../../components/Toast";
import PasswordValidator from "../../components/PasswordValidator";
import { useAuth } from "../../context/AuthContext";
import { useMetaPixel } from "../../hooks/use-meta-pixel";
import { useGoogleAnalytics } from "../../hooks/use-google-analytics";
import { useGoogleAds } from "../../hooks/use-google-ads";
import PropTypes from "prop-types";
import creditService from "../../services/creditService";
import ticketService from "../../services/ticketService";

const Settings = ({ activeTab: initialTab = "account", onSubscriptionUpdate }) => {
  const { user } = useAuth();
  const { trackPurchase } = useMetaPixel();
  const { trackPurchase: trackGAPurchase } = useGoogleAnalytics();
  const { trackPurchase: trackAdsPurchase } = useGoogleAds();
  const [loading, setLoading] = useState(false);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  // Track which specific checkout button is currently processing
  const [processingCheckoutType, setProcessingCheckoutType] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Add back the ref to track cleanup execution
  const cleanupExecutedRef = useRef(false);
  
  // CSS styles for disabled subscription plans
  const disabledSectionStyle = {
    opacity: 0.6,
    pointerEvents: 'none',
    position: 'relative'
  };
  
  // Account settings form state
  const [accountForm, setAccountForm] = useState({
    given_name: user?.given_name || "",
    family_name: user?.family_name || "",
    email: user?.email || "",
    company: user?.company || "",
    position: user?.position || "",
    phone: user?.phone || "",
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Password visibility state
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  // Products and orders state
  // eslint-disable-next-line no-unused-vars
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [credits, setCredits] = useState({
    support_credits: 0,
    custom_credits: 0
  });
  // eslint-disable-next-line no-unused-vars
  const [ticketCreditCost, setTicketCreditCost] = useState(1);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [cancelSubscriptionId, setCancelSubscriptionId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [creditHistory, setCreditHistory] = useState([]);

  // Form submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [accountErrors, setAccountErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
  
  // Add state for spent credits near other state declarations
  const [spentCredits, setSpentCredits] = useState({ support: 0, development: 0 });

  // Define all fetch functions first
  const fetchActiveSubscription = useCallback(async () => {
    setIsLoadingSubscription(true);
    try {
      const response = await orderService.getActiveSubscription();
      
      if (response && response.success) {
        if (response.has_active_subscription) {
          const subscriptionData = response.subscription;
          
          if (subscriptionData.metadata && typeof subscriptionData.metadata === 'string') {
            try {
              subscriptionData.metadata = JSON.parse(subscriptionData.metadata);
            } catch (e) {
              console.error("Failed to parse subscription metadata string:", e);
            }
          }
          
          // Convert amount and amount_paid to numbers if they exist, or default to 0
          const amount = subscriptionData.amount ? Number(subscriptionData.amount) : 0;
          const amount_paid = subscriptionData.amount_paid ? Number(subscriptionData.amount_paid) : 0;
          
          const processedSubscription = {
            ...subscriptionData,
            amount: amount || amount_paid || 0,
            amount_paid: amount_paid || amount || 0,
            start_date: subscriptionData.start_date || subscriptionData.started_at || subscriptionData.created_at,
            plan_name: subscriptionData.metadata?.plan_name || 
                      subscriptionData.product_name || 
                      subscriptionData.product?.name
          };
          
          // Ensure the subscription is in proper JSON format, not a string
          if (typeof processedSubscription === 'string') {
            try {
              setActiveSubscription(JSON.parse(processedSubscription));
            } catch (e) {
              console.error("Failed to parse subscription string:", e);
              setActiveSubscription(processedSubscription);
            }
          } else {
            setActiveSubscription(processedSubscription);
          }
        } else {
          setActiveSubscription(null);
        }
      }
    } catch (error) {
      console.error("Error fetching active subscription:", error);
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [setIsLoadingSubscription]);

  const fetchCredits = useCallback(async () => {
    try {
      const response = await creditService.getRemainingCredits(user?.id);
      console.log("Fetched credits:", response);
      if (response && response.success) {
        setCredits(response.data || { support_credits: 0, custom_credits: 0 });
      }
    } catch (error) {
      console.error("Error fetching credits:", error);
      setToast({
        open: true,
        message: "Failed to load credits. Please try again later.",
        severity: "error"
      });
    }
  }, [user?.id, setToast]);

  const fetchTicketCreditCost = useCallback(async () => {
    try {
      const response = await productService.getTicketCreditCost();
      
      if (response && response.success) {
        setTicketCreditCost(response.creditCost);
      }
    } catch (error) {
      console.error("Error fetching ticket credit cost:", error);
    }
  }, []);

  const fetchCreditHistory = useCallback(async () => {
    try {
      const response = await creditService.getCreditHistory(user?.id, { limit: 5 });
      if (response && response.success) {
        setCreditHistory(response.history || []);
      }
    } catch (error) {
      console.error("Error fetching credit history:", error);
      setToast({
        open: true,
        message: "Failed to load credit history. Please try again later.",
        severity: "error"
      });
    }
  }, [user?.id, setToast]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await productService.getAllProducts({
        limit: 100,
        include_archived: false
      });

      if (response && response.success) {
        setProducts(response.products || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setToast({
        open: true,
        message: "Failed to load products. Please try again later.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [setToast]);
  
  // Update activeTab when initialTab prop changes or URL query parameter changes
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get("tab");
    const customerSessionToken = queryParams.get("checkout_id");
    
    // Process URL parameters
    const handleUrlCleanup = async () => {
      // Check if cleanup has already been executed
      if (cleanupExecutedRef.current) {
        console.log("Cleanup already executed, skipping...");
        return;
      }
      
      // Mark cleanup as executed
      cleanupExecutedRef.current = true;
      
      if (customerSessionToken) {
        try {
          const sessionResponse = await polarService.verifyCustomerSessionToken(customerSessionToken);
          console.log('Session verification response:', sessionResponse);
          
          if (sessionResponse && sessionResponse.success && sessionResponse.session) {
            const session = sessionResponse.session;
            // Add null checks for items array
            const isSubscription = session.items && Array.isArray(session.items) && 
              session.items.some(item => item && item.type === 'subscription');
            const productName = session.items?.[0]?.product?.name || 'product';
            const totalAmount = session.total_amount;
            const currency = session.currency;
            
            const formattedAmount = (totalAmount / 100).toFixed(2);
            const successMessage = isSubscription 
              ? `Successfully subscribed to ${productName} for ${currency} ${formattedAmount}`
              : `Successfully purchased ${productName} for ${currency} ${formattedAmount}`;

            setToast({
              open: true,
              message: successMessage,
              severity: "success"
            });

            // Track purchase/subscription with Meta Pixel
            trackPurchase(formattedAmount, currency);

            // Track purchase/subscription with Google Analytics
            trackGAPurchase(formattedAmount, currency);

            // Track purchase/subscription with Google Ads
            trackAdsPurchase(formattedAmount, currency, {
              transaction_id: Date.now().toString(),
              product_type: 'subscription',
              items: [{
                item_id: 'subscription',
                item_name: 'Subscription Plan',
                category: 'subscription',
                quantity: 1,
                price: formattedAmount
              }]
            });

            // Instead of calling fetchCredits separately here, we'll handle it differently
            // First, recalculate credits to ensure server-side accuracy after purchase
            await creditService.recalculateCredits(user?.id);
            
            // Then refresh all the relevant data
            await Promise.all([
              fetchProducts(),
              fetchActiveSubscription(),
              fetchTicketCreditCost()
              // fetchCredits is removed from here to prevent double counting
            ]);
            
            // Now fetch credits separately with the correct values
            const updatedCredits = await creditService.getRemainingCredits(user?.id);
            if (updatedCredits && updatedCredits.success) {
              setCredits(updatedCredits.data || { support_credits: 0, custom_credits: 0 });
              
              // Dispatch a custom event to notify DashboardLayout about credit updates
              const dashboardUpdateEvent = new CustomEvent('dashboardCreditsUpdate', {
                detail: {
                  userId: user?.id,
                  credits: {
                    support: updatedCredits.data.support_credits,
                    development: updatedCredits.data.custom_credits
                  }
                }
              });
              
              window.dispatchEvent(dashboardUpdateEvent);
            }

            // Scroll to relevant section and highlight it
            const sectionId = isSubscription ? 'subscriptions' : 'credits';
            const section = document.getElementById(sectionId);
            if (section) {
              section.scrollIntoView({ behavior: 'smooth' });
              section.classList.add('highlight-section');
              setTimeout(() => section.classList.remove('highlight-section'), 2000);
            }
          }
        } catch (error) {
          console.error('Error verifying session:', error);
          setToast({
            open: true,
            message: "Failed to verify purchase. Please contact support if this persists.",
            severity: "warning"
          });
        }
      }

      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('checkout_id');
      newUrl.searchParams.delete('checkout_result');
      newUrl.searchParams.delete('customer_session_token');
      window.history.replaceState({}, '', newUrl);
    };
    
    handleUrlCleanup();
    setActiveTab(tab || initialTab);
  }, [initialTab, location.search, fetchProducts, fetchActiveSubscription, fetchTicketCreditCost, user?.id, trackPurchase, trackGAPurchase, trackAdsPurchase]); // Added trackAdsPurchase

  // Declare all fetching functions with useCallback before using them
  const fetchSubscriptions = useCallback(async () => {
    try {
      const response = await orderService.getUserSubscriptions({
        limit: 100
      });

      if (response && response.success) {
        // Process subscriptions to handle metadata and correct field mapping
        const processedSubscriptions = (response.subscriptions || []).map(subscription => {
          // If there's metadata but it's a string, try to parse it
          if (subscription.metadata && typeof subscription.metadata === 'string') {
            try {
              subscription.metadata = JSON.parse(subscription.metadata);
            } catch (e) {
              console.error("Failed to parse subscription metadata string:", e);
            }
          }
          
          // Convert amount and amount_paid to numbers if they exist, or default to 0
          const amount = subscription.amount ? Number(subscription.amount) : 0;
          const amount_paid = subscription.amount_paid ? Number(subscription.amount_paid) : 0;
          
          // Add derived fields for compatibility
          return {
            ...subscription,
            // If amount is missing but amount_paid exists, use that, ensure it's a number
            amount: amount || amount_paid || 0,
            amount_paid: amount_paid || amount || 0,
            // If start_date is missing but started_at or created_at exists, use those
            start_date: subscription.start_date || subscription.started_at || subscription.created_at,
            // Extract plan information from metadata if available
            plan_name: subscription.metadata?.plan_name || 
                      subscription.product_name || 
                      subscription.product?.name,
            // Format dates
            created_at: subscription.created_at ? new Date(subscription.created_at).toISOString() : null,
            current_period_end: subscription.current_period_end ? new Date(subscription.current_period_end).toISOString() : null,
            // Ensure status is available
            status: subscription.status || 'active'
          };
        });
        
        setSubscriptions(processedSubscriptions);
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      setToast({
        open: true,
        message: "Failed to load subscriptions. Please try again later.",
        severity: "error"
      });
    }
  }, [setToast]);

  const fetchOrders = useCallback(async () => {
    console.log('fetchOrders');
    try {
      const response = await orderService.getUserOrders({
        limit: 100
      });

      if (response && response.success) {
        // Process orders to ensure consistent data structure
        const processedOrders = (response.orders || []).map(order => {
          // If there's metadata but it's a string, try to parse it
          if (order.metadata && typeof order.metadata === 'string') {
            try {
              order.metadata = JSON.parse(order.metadata);
            } catch (e) {
              console.error("Failed to parse order metadata string:", e);
            }
          }
          
          // Convert amounts to numbers if they exist, or default to 0
          const total = order.total_amount ? Number(order.total_amount) : 0;
          const amount = order.amount ? Number(order.amount) : 0;
          const amount_paid = order.amount_paid ? Number(order.amount_paid) : 0;
          
          // Add derived fields for compatibility
          return {
            ...order,
            // Ensure amount is available and is a number
            total_amount: total || amount || amount_paid || 0,
            amount: amount || 0,
            amount_paid: amount_paid || 0,
            // Format dates
            created_at: order.created_at ? new Date(order.created_at).toISOString() : null,
            // Ensure product name is available
            product_name: order.metadata?.product_name || 
                        order.product_name || 
                        order.product?.name || 
                        'Product',
            // Ensure status is available
            status: order.status || 'completed'
          };
        });
        
        setOrders(processedOrders);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setToast({
        open: true,
        message: "Failed to load orders. Please try again later.",
        severity: "error"
      });
    }
  }, [setToast]);

  // Update useEffect dependencies
  useEffect(() => {
    // Flag to track if this is the first load of the component
    // eslint-disable-next-line no-unused-vars
    const isFirstLoad = !cleanupExecutedRef.current;
    
    const fetchData = async () => {
      if (user?.id) {
        try {
          // Destructure only the ticketsResponse which is used
          // If URL has checkout_id parameter, skip fetchCredits here as it will be handled by handleUrlCleanup
          const urlParams = new URLSearchParams(location.search);
          const hasCheckoutParam = urlParams.has("checkout_id");
          
          // If there was a recent checkout completion, don't fetch credits again
          const promises = [
            fetchActiveSubscription(),
            hasCheckoutParam ? Promise.resolve() : fetchCredits(), // Skip fetchCredits if we have checkout_id
            fetchProducts(),
            fetchTicketCreditCost(),
            fetchCreditHistory(),
            fetchSubscriptions(),
            fetchOrders(),
            ticketService.getAllTickets({ limit: 9999999 })
          ];
          
          const [, , , , , , , ticketsResponse] = await Promise.all(promises);

          console.log('ticketsResponse',ticketsResponse);

          // Calculate spent credits from confirmed tickets
          if (ticketsResponse && ticketsResponse.success) {
            const confirmedTickets = ticketsResponse.tickets.filter(ticket => ticket.credit_confirmed);
            const totalSpent = confirmedTickets.reduce((acc, ticket) => ({
              support: acc.support + (ticket.support_credits || 0),
              development: acc.development + (ticket.custom_credits || 0)
            }), { support: 0, development: 0 });
            
            setSpentCredits(totalSpent);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
    };
    
    fetchData();
  }, [user?.id, fetchActiveSubscription, fetchCredits, fetchProducts, fetchTicketCreditCost, fetchCreditHistory, fetchSubscriptions, fetchOrders, location.search]);

  const handleRecalculateCredits = async () => {
    try {
      setLoading(true);
      const response = await creditService.recalculateCredits(user?.id);

      if (response && response.success) {
        const updatedCredits = await creditService.getRemainingCredits(user?.id);

        setCredits(updatedCredits.data || { support_credits: 0, custom_credits: 0 });

        // Dispatch a custom event to notify DashboardLayout
        const dashboardUpdateEvent = new CustomEvent('dashboardCreditsUpdate', {
          detail: {
            userId: user?.id,
            credits: {
              support: updatedCredits.data.support_credits,
              development: updatedCredits.data.custom_credits
            }
          }
        });

        window.dispatchEvent(dashboardUpdateEvent);

        setToast({
          open: true,
          message: "Credits recalculated successfully",
          severity: "success"
        });
      }
    } catch (error) {
      console.error("Error recalculating credits:", error);
      setToast({
        open: true,
        message: "Failed to recalculate credits. Please try again later.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle checkout for subscription plans
  const handleSubscribeCheckout = async (plan) => {
    try {
      // Check if there's an active subscription first
      if (activeSubscription) {
        setToast({
          open: true,
          message: "You already have an active subscription. Please cancel it before subscribing to a new plan.",
          severity: "warning"
        });
        return;
      }
      
      // Track both the general processing state and the specific button being processed
      setIsProcessingCheckout(true);
      setProcessingCheckoutType(`subscription-${plan}`);
      
      // Show toast to indicate processing
      setToast({
        open: true,
        message: "Preparing checkout session...",
        severity: "info"
      });
      
      // Get current user data for checkout
      const userDataResponse = await authService.getCurrentUser();
      const userData = userDataResponse?.user || {};
      
      // Get user IP address (typically handled by the server, but including for completeness)
      // For client-side implementations, we'll leave the IP address for the server to determine
      
      // Prepare subscription data
      let planData = {};
      switch(plan) {
        case 'basic':
          planData = {
            name: 'Essential Plan',
            subscription_tier_id: '47bce743-29b0-4faf-b3d7-a2c8f14e15d8',
            price: 6900 // $69.00
          };
          break;
        case 'professional':
          planData = {
            name: 'Growth Plan',
            subscription_tier_id: 'a7dcbb67-9b6e-4dbd-ac25-6a61d958a62c',
            price: 17900 // $179.00
          };
          break;
        case 'enterprise':
          planData = {
            name: 'Pro Team Plan',
            subscription_tier_id: '479f129b-4bf9-4d10-8f42-883eb936f186',
            price: 37900 // $379.00
          };
          break;
        default:
          throw new Error('Invalid plan selected');
      }
      
      // Create checkout session - using checkout_id for success path
      const response = await polarService.createCheckoutSession({
        success_url: `${window.location.origin}/dashboard/settings?tab=products&checkout_id={CHECKOUT_ID}`,
        cancel_url: `${window.location.origin}/dashboard/settings?tab=products&checkout_result=cancelled`,
        mode: 'subscription',
        subscription_tier_id: planData.subscription_tier_id,
        metadata: {
          plan_name: planData.name,
          user_id: user?.id,
          purchase_type: 'subscription'
        },
        customer_name: `${userData.given_name || ''} ${userData.family_name || ''}`.trim(),
        customer_email: userData.email,
        customer_ip_address: null // Let server determine this
      });
      
      console.log("Subscription checkout response:", response);
      
      // Redirect to checkout URL
      if (response.success && (response.checkoutUrl || response.checkout_url || (response.session && response.session.url))) {
        // Update toast to indicate successful redirection
        setToast({
          open: true,
          message: "Redirecting to secure checkout...",
          severity: "success"
        });
        
        // Use the URL from the response, handling different response formats
        const checkoutUrl = response.checkoutUrl || response.checkout_url || response.session.url;
        
        // Add a small delay for the transition message to be visible
        setTimeout(() => {
          window.location.href = checkoutUrl;
        }, 1500); // 1.5 second delay for smooth transition
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setToast({
        open: true,
        message: error.message || "Failed to initiate checkout. Please try again later.",
        severity: "error"
      });
    } finally {
      setIsProcessingCheckout(false);
      setProcessingCheckoutType(null);
    }
  };

  // Handle checkout for one-time fix
  const handleOneTimeFixCheckout = async () => {
    try {
      // Check if there's an active subscription first
      if (activeSubscription) {
        setToast({
          open: true,
          message: "You already have an active subscription. No need for one-time fixes.",
          severity: "info"
        });
        return;
      }
      
      setIsProcessingCheckout(true);
      setProcessingCheckoutType('one-time-fix');
      
      // Show toast to indicate processing
      setToast({
        open: true,
        message: "Preparing checkout session...",
        severity: "info"
      });
      
      // Get current user data for checkout
      const userDataResponse = await authService.getCurrentUser();
      const userData = userDataResponse?.user || {};
      
      // Create checkout session for one-time fix - using checkout_id
      const response = await polarService.createCheckoutSession({
        success_url: `${window.location.origin}/dashboard/settings?tab=products&checkout_id={CHECKOUT_ID}`,
        cancel_url: `${window.location.origin}/dashboard/settings?tab=products&checkout_result=cancelled`,
        mode: 'one_time',
        price_id: '3ed45779-28ac-42b7-af58-9553699bf0a0',
        metadata: {
          product_name: 'One-time Fix Support',
          user_id: user?.id,
          purchase_type: 'one_time_fix',
          credit_type: 'support'
        },
        customer_name: `${userData.given_name || ''} ${userData.family_name || ''}`.trim(),
        customer_email: userData.email,
        customer_ip_address: null // Let server determine this
      });

      console.log("One-time fix checkout response:", response);
      
      // Redirect to checkout URL
      if (response.success && (response.checkoutUrl || response.checkout_url || (response.session && response.session.url))) {
        // Update toast to indicate successful redirection
        setToast({
          open: true,
          message: "Redirecting to secure checkout...",
          severity: "success"
        });
        
        // Use the URL from the response, handling different response formats
        const checkoutUrl = response.checkoutUrl || response.checkout_url || response.session.url;
        
        // Add a small delay for the transition message to be visible
        setTimeout(() => {
          window.location.href = checkoutUrl;
        }, 1500); // 1.5 second delay for smooth transition
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setToast({
        open: true,
        message: error.message || "Failed to initiate checkout. Please try again later.",
        severity: "error"
      });
    } finally {
      setIsProcessingCheckout(false);
      setProcessingCheckoutType(null);
    }
  };
  
  // Handle checkout for add-on credits
  const handleAddOnCheckout = async (type, quantity, price) => {
    try {
      if (!activeSubscription) {
        setToast({
          open: true,
          message: "You need an active subscription to purchase add-ons.",
          severity: "warning"
        });
        return;
      }
      
      setIsProcessingCheckout(true);
      setProcessingCheckoutType(`addon-${type}-${quantity}`);
      
      // Get current user data for checkout
      const userDataResponse = await authService.getCurrentUser();
      const userData = userDataResponse?.user || {};

      // Map type and quantity to price ID
      let priceId;
      
      if (type === 'support') {
        // Support credits
        switch(quantity) {
          case 3:
            priceId = '2fada8f2-99bf-40a0-b6a5-cc6a9f53b080';
            break;
          case 10:
            priceId = '0f872c41-b069-424b-938f-c5dee512d762';
            break;
          case 20:
            priceId = '0414c9f3-5dee-4837-8c68-c88d301477da';
            break;
          default:
            throw new Error('Invalid support credit quantity');
        }
      } else if (type === 'development') {
        // Development credits
        switch(quantity) {
          case 5:
            priceId = 'fa184360-8ecb-4cf4-bff2-e8ab7c2eb0bf';
            break;
          case 10:
            priceId = '1a13444c-f0ce-4d96-babb-cd87439d2900';
            break;
          case 20:
            priceId = '10982165-bab6-4639-9687-097dae1bed30';
            break;
          default:
            throw new Error('Invalid development credit quantity');
        }
      } else {
        throw new Error('Invalid credit type');
      }
      
      // Show toast to indicate processing
      setToast({
        open: true,
        message: "Preparing checkout session...",
        severity: "info"
      });
      
      // Create checkout session for add-on - using checkout_id
      const response = await polarService.createCheckoutSession({
        success_url: `${window.location.origin}/dashboard/settings?tab=products&checkout_id={CHECKOUT_ID}`,
        cancel_url: `${window.location.origin}/dashboard/settings?tab=products&checkout_result=cancelled`,
        mode: 'one_time',
        price_id: priceId,
        metadata: {
          product_name: `${quantity} ${type} Credits Add-on`,
          subscription_id: activeSubscription.id,
          user_id: user?.id,
          credit_type: type,
          credit_quantity: quantity,
          purchase_type: 'credits_addon'
        },
        customer_name: `${userData.given_name || ''} ${userData.family_name || ''}`.trim(),
        customer_email: userData.email,
        customer_ip_address: null // Let server determine this
      });
      
      console.log(`${type} credits add-on checkout response:`, response);
      
      // Redirect to checkout URL
      if (response.success && (response.checkoutUrl || response.checkout_url || (response.session && response.session.url))) {
        // Update toast to indicate successful redirection
        setToast({
          open: true,
          message: "Redirecting to secure checkout...",
          severity: "success"
        });
        
        // Use the URL from the response, handling different response formats
        const checkoutUrl = response.checkoutUrl || response.checkout_url || response.session.url;
        
        // Add a small delay for the transition message to be visible
        setTimeout(() => {
          window.location.href = checkoutUrl;
        }, 1500); // 1.5 second delay for smooth transition
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error("Add-on checkout error:", error);
      setToast({
        open: true,
        message: error.message || "Failed to initiate checkout. Please try again later.",
        severity: "error"
      });
    } finally {
      setIsProcessingCheckout(false);
      setProcessingCheckoutType(null);
    }
  };

  // Handle cancel subscription
  const handleCancelSubscription = async () => {
    if (!cancelSubscriptionId) return;

    try {
      setIsCancelling(true);
      const response = await orderService.cancelSubscription(cancelSubscriptionId);
      
      if (response && response.success) {
        setToast({
          open: true,
          message: "Subscription canceled successfully. Please note that subscription plan credits will be lost immediately, however any one-time purchase credits will remain.",
          severity: "warning"
        });
        
        // First notify parent component to refresh subscription data
        if (typeof onSubscriptionUpdate === 'function') {
          console.log('Calling onSubscriptionUpdate...');
          onSubscriptionUpdate();
        }

        // Then recalculate credits to ensure accurate credit count
        console.log('Recalculating credits after subscription cancellation...');
        const recalcResponse = await creditService.recalculateCredits(user?.id);
        
        if (recalcResponse && recalcResponse.success) {
          // Fetch the updated credits
          const updatedCredits = await creditService.getRemainingCredits(user?.id);
          
          if (updatedCredits && updatedCredits.success) {
            // Update local state
            setCredits(updatedCredits.data || { support_credits: 0, custom_credits: 0 });
            
            // Dispatch a custom event to notify DashboardLayout about credit updates
            const dashboardUpdateEvent = new CustomEvent('dashboardCreditsUpdate', {
              detail: {
                userId: user?.id,
                credits: {
                  support: updatedCredits.data.support_credits,
                  development: updatedCredits.data.custom_credits
                }
              }
            });
            
            window.dispatchEvent(dashboardUpdateEvent);
          }
        }

        // Also refetch local subscription data
        console.log('Refetching local subscription data...');
        await Promise.all([
          fetchSubscriptions(),
          fetchActiveSubscription()
        ]);
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      setToast({
        open: true,
        message: "Failed to cancel subscription. Please try again later.",
        severity: "error"
      });
    } finally {
      setIsCancelling(false);
      setConfirmCancel(false);
      setCancelSubscriptionId(null);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Handle account form changes
  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountForm({
      ...accountForm,
      [name]: value,
    });
    
    // Clear errors when the user starts typing
    if (accountErrors[name]) {
      setAccountErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({
      ...passwordForm,
      [name]: value,
    });
    
    // Clear errors when the user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle form submissions
  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateAccountForm();
    if (Object.keys(errors).length > 0) {
      setAccountErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare update data - exclude email if it's a Google account
      const updateData = {
        given_name: accountForm.given_name,
        family_name: accountForm.family_name,
        company: accountForm.company,
        position: accountForm.position,
        phone: accountForm.phone,
      };
      
      // Send request to API
      const response = await authService.updateProfile(updateData);
      
      if (response && response.success) {
        setToast({
          open: true,
          message: "Profile updated successfully!",
          severity: "success"
        });
      } else {
        throw new Error("Failed to update profile. Please try again.");
      }
    } catch (err) {
      console.error("Profile update error:", err);
      
      // Extract error details
      let errorMessage = "Failed to update profile. Please try again.";
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      setToast({
        open: true,
        message: errorMessage,
        severity: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validate account form
  const validateAccountForm = () => {
    const errors = {};
    
    if (!accountForm.given_name.trim()) {
      errors.given_name = "First name is required";
    }
    
    if (!accountForm.family_name.trim()) {
      errors.family_name = "Last name is required";
    }
    
    return errors;
  };

  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordForm.currentPassword) {
      errors.currentPassword = "Current password is required";
    }
    
    if (!passwordForm.newPassword) {
      errors.newPassword = "New password is required";
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters long";
    } else if (!/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/.test(passwordForm.newPassword)) {
      errors.newPassword = "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    
    return errors;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await authService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );
      
      if (response.success) {
        setToast({
          open: true,
          message: "Password updated successfully!",
          severity: "success"
        });
        
        // Reset the form
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        throw new Error("Failed to update password. Please try again.");
      }
    } catch (err) {
      console.error("Password change error:", err);
      
      // Extract error details
      let errorMessage = "Failed to update password. Please try again.";
      let errorCode = "";
      
      if (typeof err.message === 'string' && err.message.startsWith('{')) {
        try {
          const parsedError = JSON.parse(err.message);
          errorCode = parsedError.code;
          errorMessage = parsedError.message;
        } catch (e) {
          console.log('Failed to parse error JSON');
        }
      } else if (err.code && err.message) {
        errorCode = err.code;
        errorMessage = err.message;
      }
      
      // Show appropriate error message
      if (errorCode === 'INCORRECT_PASSWORD') {
        setPasswordErrors({
          currentPassword: "Current password is incorrect"
        });
      } else {
        setToast({
          open: true,
          message: errorMessage,
          severity: "error"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateString}`);
        return 'N/A';
      }
      
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    } catch (error) {
      console.error(`Error formatting date ${dateString}:`, error);
      return 'N/A';
    }
  };

  // Get subscription status badge color based on status
  // eslint-disable-next-line no-unused-vars
  const getSubscriptionStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceling':
      case 'cancelling':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Add a function to handle invoice download
  const handleDownloadInvoice = async (orderId) => {
    try {
      setLoading(true);
      const response = await orderService.getOrderInvoice(orderId);
      
      if (response && response.success && response.invoice_url) {
        // Open invoice URL in new tab
        window.open(response.invoice_url, '_blank');
      } else {
        throw new Error('No invoice URL available');
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      setToast({
        open: true,
        message: "Failed to download invoice. Please try again later.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Add a button to recalculate credits in the Credits Summary section
  const renderCreditsSummary = () => (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium">Credits Summary</h2>
        <p className="text-sm text-gray-500">Your current credits and usage</p>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <h3 className="text-sm font-medium text-gray-700">Support Credits</h3>
            <p className="text-2xl font-bold text-blue-600 mt-1">{credits.support_credits || 0}</p>
            <p className="text-xs text-gray-500 mt-2">For maintenance, bug fixes, and updates</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-100">
            <h3 className="text-sm font-medium text-gray-700">Custom Development Credits</h3>
            <p className="text-2xl font-bold text-green-600 mt-1">{credits.custom_credits || 0}</p>
            <p className="text-xs text-gray-500 mt-2">For custom features and development</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
            <h3 className="text-sm font-medium text-gray-700">Credits Spent</h3>
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Support:</span>
                <span className="text-lg font-bold text-purple-600">{spentCredits.support}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Development:</span>
                <span className="text-lg font-bold text-purple-600">{spentCredits.development}</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Total credits spent on confirmed tickets</p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleRecalculateCredits}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                Recalculating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Recalculate Credits
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Display loading state while fetching profile
  if (loading) {
    return (
      <div className="container p-4 mx-auto flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-gray-600">Loading information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container p-4 mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-gray-500">Manage your account, orders, and subscriptions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Sidebar Navigation */}
        <div className="md:col-span-1">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <nav className="flex flex-col">
              <button 
                onClick={() => { 
                  setActiveTab("account");
                  navigate("/dashboard/settings?tab=account", { replace: true });
                }}
                className={`flex items-center px-4 py-3 border-l-4 ${activeTab === "account" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-transparent hover:bg-gray-50 text-gray-700"} font-medium w-full text-left`}
              >
                <User className="h-5 w-5 mr-3" />
                Account Settings
              </button>
              <button 
                onClick={() => { 
                  setActiveTab("products");
                  navigate("/dashboard/settings?tab=products", { replace: true });
                }}
                className={`flex items-center px-4 py-3 border-l-4 ${activeTab === "products" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-transparent hover:bg-gray-50 text-gray-700"} font-medium w-full text-left`}
              >
                <Package className="h-5 w-5 mr-3" />
                Products & Plans
              </button>
              <button 
                onClick={() => { 
                  setActiveTab("orders");
                  navigate("/dashboard/settings?tab=orders", { replace: true });
                }}
                className={`flex items-center px-4 py-3 border-l-4 ${activeTab === "orders" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-transparent hover:bg-gray-50 text-gray-700"} font-medium w-full text-left`}
              >
                <Receipt className="h-5 w-5 mr-3" />
                Orders & Subscriptions
              </button>
            </nav>
          </div>
        </div>

        {/* Settings Forms */}
        <div className="md:col-span-2 space-y-6">
          {/* Account Settings */}
          {activeTab === "account" && (
            <>
              <div id="account" className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium">Account Settings</h2>
                  <p className="text-sm text-gray-500">Update your profile information</p>
                </div>
                <div className="p-6">
                  <form onSubmit={handleAccountSubmit}>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="given_name" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                        <input
                          type="text"
                          id="given_name"
                          name="given_name"
                          value={accountForm.given_name}
                          onChange={handleAccountChange}
                          className={`bg-gray-50 border ${accountErrors.given_name ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                          required
                        />
                        {accountErrors.given_name && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {accountErrors.given_name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="family_name" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                        <input
                          type="text"
                          id="family_name"
                          name="family_name"
                          value={accountForm.family_name}
                          onChange={handleAccountChange}
                          className={`bg-gray-50 border ${accountErrors.family_name ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                          required
                        />
                        {accountErrors.family_name && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {accountErrors.family_name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={accountForm.email}
                          onChange={handleAccountChange}
                          disabled={true}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 disabled:bg-gray-100 disabled:text-gray-500"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">Email address cannot be changed</p>
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={accountForm.phone}
                          onChange={handleAccountChange}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={accountForm.company}
                          onChange={handleAccountChange}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        />
                      </div>
                      <div>
                        <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                        <input
                          type="text"
                          id="position"
                          name="position"
                          value={accountForm.position}
                          onChange={handleAccountChange}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        />
                      </div>
                    </div>
                    <div className="mt-6">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="animate-spin h-4 w-4 mr-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Security Settings */}
              <div id="security" className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium">Security</h2>
                  <p className="text-sm text-gray-500">Update your password</p>
                </div>
                <div className="p-6">
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                        <div className="relative">
                          <input
                            type={passwordVisibility.currentPassword ? "text" : "password"}
                            id="currentPassword"
                            name="currentPassword"
                            value={passwordForm.currentPassword}
                            onChange={handlePasswordChange}
                            className={`bg-gray-50 border ${passwordErrors.currentPassword ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10`}
                            required
                          />
                          <button 
                            type="button"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                            onClick={() => togglePasswordVisibility('currentPassword')}
                            tabIndex={-1}
                          >
                            {passwordVisibility.currentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {passwordErrors.currentPassword && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {passwordErrors.currentPassword}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                        <div className="relative">
                          <input
                            type={passwordVisibility.newPassword ? "text" : "password"}
                            id="newPassword"
                            name="newPassword"
                            value={passwordForm.newPassword}
                            onChange={handlePasswordChange}
                            className={`bg-gray-50 border ${passwordErrors.newPassword ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10`}
                            required
                          />
                          <button 
                            type="button"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                            onClick={() => togglePasswordVisibility('newPassword')}
                            tabIndex={-1}
                          >
                            {passwordVisibility.newPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {passwordErrors.newPassword && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {passwordErrors.newPassword}
                          </p>
                        )}
                      </div>

                      {passwordForm.newPassword && (
                        <PasswordValidator password={passwordForm.newPassword} />
                      )}

                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                        <div className="relative">
                          <input
                            type={passwordVisibility.confirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={passwordForm.confirmPassword}
                            onChange={handlePasswordChange}
                            className={`bg-gray-50 border ${passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10`}
                            required
                          />
                          <button 
                            type="button"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                            onClick={() => togglePasswordVisibility('confirmPassword')}
                            tabIndex={-1}
                          >
                            {passwordVisibility.confirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {passwordErrors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {passwordErrors.confirmPassword}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="animate-spin h-4 w-4 mr-2" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <Shield className="h-4 w-4 mr-2" />
                            Update Password
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </>
          )}

          {/* Products & Plans */}
          {activeTab === "products" && (
            <>
              {renderCreditsSummary()}

              {/* Active Subscription */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium">Current Plan</h2>
                  <p className="text-sm text-gray-500">
                    {isLoadingSubscription
                      ? "Fetching subscription details..."
                      : activeSubscription 
                        ? "Your active subscription details" 
                        : "You don't have an active subscription plan"}
                  </p>
                </div>
                <div className="p-6">
                  {isLoadingSubscription ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
                      <p className="text-gray-600 font-medium">Syncing subscription details...</p>
                      <p className="text-sm text-gray-500 mt-1">This may take a moment</p>
                    </div>
                  ) : activeSubscription ? (
                    <div className="border rounded-lg p-4 relative">
                      <div className="absolute top-0 right-0 mt-4 mr-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          activeSubscription.status === 'active' 
                            ? 'bg-green-100 text-green-800'
                            : activeSubscription.status === 'canceled' || activeSubscription.cancel_at_period_end
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}>
                          {activeSubscription.status === 'active' 
                            ? 'Active'
                            : activeSubscription.status === 'canceled' 
                              ? 'Canceling at Period End'
                              : activeSubscription.cancel_at_period_end
                                ? 'Canceling at Period End'
                                : activeSubscription.status}
                        </span>
                      </div>
                      <h3 className="text-lg font-medium">
                        {activeSubscription.metadata?.plan_name || 
                         activeSubscription.product_name || 
                         activeSubscription.product?.name || 
                         'Subscription'}
                      </h3>
                      
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Subscription ID</p>
                          <p className="font-medium">{activeSubscription.id}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Price</p>
                          <p className="font-medium">
                            ${(((activeSubscription.amount || activeSubscription.amount_paid || 0) / 100) || 0).toFixed(2)} / 
                            {activeSubscription.recurring_interval === 'month' ? 'Monthly' : 
                              activeSubscription.recurring_interval === 'year' ? 'Yearly' :
                              activeSubscription.recurring_interval || 'Period'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Start Date</p>
                          <p className="font-medium">{formatDate(activeSubscription.started_at || activeSubscription.start_date || activeSubscription.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">
                            {(activeSubscription.status === 'canceled' || activeSubscription.cancel_at_period_end) ? 'Ends On' : 'Next Billing Date'}
                          </p>
                          <p className="font-medium">{formatDate(activeSubscription.current_period_end)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Credits Per Period</p>
                          <p className="font-medium">
                            {activeSubscription.support_credits || 
                             activeSubscription.credits || 
                             (activeSubscription.metadata?.plan_name === 'Growth Plan' ? '10' : 
                              activeSubscription.metadata?.plan_name === 'Essential Plan' ? '3' : 
                              activeSubscription.metadata?.plan_name === 'Pro Team Plan' ? 'Unlimited' : 'N/A')}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Payment Status</p>
                          <p className="font-medium capitalize">{activeSubscription.payment_status || activeSubscription.status || 'N/A'}</p>
                        </div>
                      </div>

                      {activeSubscription.status === 'active' && !activeSubscription.cancel_at_period_end && (
                        <div className="mt-6">
                          {confirmCancel && cancelSubscriptionId === activeSubscription.id ? (
                            <div className="bg-red-50 p-4 rounded-md border border-red-200 text-sm">
                              <p className="font-medium text-red-800 mb-2">Are you sure you want to cancel?</p>
                              <p className="text-red-700 mb-4">Once canceled, your subscription will end immediately and any remaining subscription credits will be lost. However, any credits from one-time purchases will remain available in your account.</p>
                              <div className="flex gap-3">
                                <button
                                  onClick={handleCancelSubscription}
                                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:bg-red-400"
                                  disabled={isCancelling}
                                >
                                  {isCancelling ? (
                                    <span className="flex items-center">
                                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                      Canceling...
                                    </span>
                                  ) : 'Yes, Cancel Subscription'}
                                </button>
                                <button
                                  onClick={() => {
                                    setConfirmCancel(false);
                                    setCancelSubscriptionId(null);
                                  }}
                                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
                                >
                                  No, Keep Subscription
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setConfirmCancel(true);
                                setCancelSubscriptionId(activeSubscription.id);
                              }}
                              className="px-4 py-2 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50"
                            >
                              Cancel Subscription
                            </button>
                          )}
                        </div>
                      )}
                      
                      {activeSubscription && !confirmCancel && !activeSubscription.cancel_at_period_end && (
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                          <h4 className="font-medium text-blue-800 mb-2">Subscription Management</h4>
                          <ul className="space-y-1 text-sm text-blue-700">
                            <li className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                              <span>You can purchase additional credits as add-ons below</span>
                            </li>
                            <li className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                              <span>To change your plan, cancel your current subscription and subscribe to a new plan after the current period ends</span>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Package className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No active subscription</h3>
                      <p className="mt-1 text-sm text-gray-500">Select a plan below to subscribe.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Available Plans */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium">Available Plans</h2>
                  <p className="text-sm text-gray-500">
                    {isLoadingSubscription 
                      ? "Checking subscription status..." 
                      : "Choose a plan that works for you"}
                  </p>
                </div>
                <div className="p-6">
                  {isLoadingSubscription ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-3" />
                      <p className="text-gray-600">Verifying subscription status...</p>
                    </div>
                  ) : activeSubscription && (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="text-md font-medium text-yellow-800">You already have an active subscription</h3>
                          <p className="mt-1 text-sm text-yellow-700">
                            You cannot subscribe to another plan while you have an active subscription. 
                            You can cancel your current subscription and subscribe to a different plan 
                            after your current billing period ends.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">Subscription Plans</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={activeSubscription ? disabledSectionStyle : {}}>
                      <div className="border rounded-lg p-5 hover:shadow-md transition-shadow">
                        <h4 className="text-lg font-semibold">Essential Plan</h4>
                        <p className="text-2xl font-bold mt-2">$69<span className="text-sm font-normal text-gray-500">/month</span></p>
                        <ul className="mt-4 space-y-2">
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>3 support credits per month</span>
                          </li>
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Ideal for small businesses</span>
                          </li>
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Basic website maintenance</span>
                          </li>
                        </ul>
                        <button 
                          onClick={() => handleSubscribeCheckout('basic')}
                          disabled={isProcessingCheckout || activeSubscription}
                          className={`mt-4 w-full py-2 px-4 rounded-md text-sm font-medium ${
                            activeSubscription 
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {processingCheckoutType === 'subscription-basic' ? 'Processing...' : activeSubscription ? 'Already Subscribed' : 'Subscribe'}
                        </button>
                      </div>
                      
                      <div className="border-2 border-blue-500 rounded-lg p-5 shadow-md relative">
                        <div className="absolute -top-3 right-3 bg-blue-500 text-white text-xs px-2 py-1 rounded">Popular</div>
                        <h4 className="text-lg font-semibold">Growth Plan</h4>
                        <p className="text-2xl font-bold mt-2">$179<span className="text-sm font-normal text-gray-500">/month</span></p>
                        <ul className="mt-4 space-y-2">
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>10 support credits per month</span>
                          </li>
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>5 custom development credits</span>
                          </li>
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Perfect for growing businesses</span>
                          </li>
                        </ul>
                        <button 
                          onClick={() => handleSubscribeCheckout('professional')}
                          disabled={isProcessingCheckout || activeSubscription}
                          className={`mt-4 w-full py-2 px-4 rounded-md text-sm font-medium ${
                            activeSubscription 
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {processingCheckoutType === 'subscription-professional' ? 'Processing...' : activeSubscription ? 'Already Subscribed' : 'Subscribe'}
                        </button>
                      </div>
                      
                      <div className="border rounded-lg p-5 hover:shadow-md transition-shadow">
                        <h4 className="text-lg font-semibold">Pro Team Plan</h4>
                        <p className="text-2xl font-bold mt-2">$379<span className="text-sm font-normal text-gray-500">/month</span></p>
                        <ul className="mt-4 space-y-2">
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>30 support credits per month</span>
                          </li>
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>10 custom development credits</span>
                          </li>
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Best for multiple websites</span>
                          </li>
                        </ul>
                        <button 
                          onClick={() => handleSubscribeCheckout('enterprise')}
                          disabled={isProcessingCheckout || activeSubscription}
                          className={`mt-4 w-full py-2 px-4 rounded-md text-sm font-medium ${
                            activeSubscription 
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {processingCheckoutType === 'subscription-enterprise' ? 'Processing...' : activeSubscription ? 'Already Subscribed' : 'Subscribe'}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">One-time Purchase</h3>
                    {activeSubscription ? (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-2 text-gray-500" />
                          You already have an active subscription. No need for one-time purchases.
                        </p>
                      </div>
                    ) : (
                      <div className="border rounded-lg p-5 hover:shadow-md transition-shadow max-w-md">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-lg font-semibold">One-time Fix Support</h4>
                            <p className="text-2xl font-bold mt-2">$89</p>
                          </div>
                          <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">One-time</span>
                        </div>
                        <ul className="mt-4 space-y-2">
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>3 support credits (good for a single issue fix)</span>
                          </li>
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Credits never expire</span>
                          </li>
                          <li className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Quick response for urgent issues</span>
                          </li>
                          <li className="flex items-start text-sm text-amber-700">
                            <AlertCircle className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span>For issues requiring more than 3 credits, consider purchasing additional one-time credits or a subscription plan</span>
                          </li>
                        </ul>
                        <button 
                          onClick={handleOneTimeFixCheckout}
                          disabled={isProcessingCheckout}
                          className="mt-4 w-full py-2 px-4 rounded-md bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium"
                        >
                          {processingCheckoutType === 'one-time-fix' ? 'Processing...' : 'Purchase'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">Subscription Add-Ons</h3>
                    {isLoadingSubscription ? (
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center">
                          <Loader2 className="h-4 w-4 animate-spin text-blue-500 mr-2" />
                          <p className="text-sm text-blue-800">Checking subscription status...</p>
                        </div>
                      </div>
                    ) : !activeSubscription ? (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-2 text-amber-500" />
                          You need an active subscription to purchase add-ons
                        </p>
                      </div>
                    ) : (
                      <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-800 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                          You can purchase additional credits for your active subscription
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-md font-semibold mb-3 text-blue-700">Support Credits</h4>
                        <div className="space-y-3">
                          <div className={`border rounded-lg p-4 flex justify-between items-center ${!activeSubscription ? 'opacity-60' : 'hover:bg-gray-50'}`}>
                            <div>
                              <p className="font-medium">3 credits</p>
                              <p className="text-sm text-gray-500">Add to your subscription</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">$69</p>
                              <button 
                                onClick={() => handleAddOnCheckout('support', 3, 69)}
                                disabled={!activeSubscription || isProcessingCheckout}
                                className={`text-xs ${!activeSubscription ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'} px-2 py-1 rounded-full`}
                              >
                                {processingCheckoutType === 'addon-support-3' ? 'Processing...' : 'Add'}
                              </button>
                            </div>
                          </div>
                          <div className={`border rounded-lg p-4 flex justify-between items-center ${!activeSubscription ? 'opacity-60' : 'hover:bg-gray-50'}`}>
                            <div>
                              <p className="font-medium">10 credits</p>
                              <p className="text-sm text-gray-500">Add to your subscription</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">$179</p>
                              <button 
                                onClick={() => handleAddOnCheckout('support', 10, 179)}
                                disabled={!activeSubscription || isProcessingCheckout}
                                className={`text-xs ${!activeSubscription ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'} px-2 py-1 rounded-full`}
                              >
                                {processingCheckoutType === 'addon-support-10' ? 'Processing...' : 'Add'}
                              </button>
                            </div>
                          </div>
                          <div className={`border rounded-lg p-4 flex justify-between items-center ${!activeSubscription ? 'opacity-60' : 'hover:bg-gray-50'}`}>
                            <div>
                              <p className="font-medium">20 credits</p>
                              <p className="text-sm text-gray-500">Add to your subscription</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">$349</p>
                              <button 
                                onClick={() => handleAddOnCheckout('support', 20, 349)}
                                disabled={!activeSubscription || isProcessingCheckout}
                                className={`text-xs ${!activeSubscription ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'} px-2 py-1 rounded-full`}
                              >
                                {processingCheckoutType === 'addon-support-20' ? 'Processing...' : 'Add'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-md font-semibold mb-3 text-emerald-700">Custom Development Credits</h4>
                        <div className="space-y-3">
                          <div className={`border rounded-lg p-4 flex justify-between items-center ${!activeSubscription ? 'opacity-60' : 'hover:bg-gray-50'}`}>
                            <div>
                              <p className="font-medium">5 credits</p>
                              <p className="text-sm text-gray-500">Add to your subscription</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">$99</p>
                              <button 
                                onClick={() => handleAddOnCheckout('development', 5, 99)}
                                disabled={!activeSubscription || isProcessingCheckout}
                                className={`text-xs ${!activeSubscription ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'} px-2 py-1 rounded-full`}
                              >
                                {processingCheckoutType === 'addon-development-5' ? 'Processing...' : 'Add'}
                              </button>
                            </div>
                          </div>
                          <div className={`border rounded-lg p-4 flex justify-between items-center ${!activeSubscription ? 'opacity-60' : 'hover:bg-gray-50'}`}>
                            <div>
                              <p className="font-medium">10 credits</p>
                              <p className="text-sm text-gray-500">Add to your subscription</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">$189</p>
                              <button 
                                onClick={() => handleAddOnCheckout('development', 10, 189)}
                                disabled={!activeSubscription || isProcessingCheckout}
                                className={`text-xs ${!activeSubscription ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'} px-2 py-1 rounded-full`}
                              >
                                {processingCheckoutType === 'addon-development-10' ? 'Processing...' : 'Add'}
                              </button>
                            </div>
                          </div>
                          <div className={`border rounded-lg p-4 flex justify-between items-center ${!activeSubscription ? 'opacity-60' : 'hover:bg-gray-50'}`}>
                            <div>
                              <p className="font-medium">20 credits</p>
                              <p className="text-sm text-gray-500">Add to your subscription</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">$369</p>
                              <button 
                                onClick={() => handleAddOnCheckout('development', 20, 369)}
                                disabled={!activeSubscription || isProcessingCheckout}
                                className={`text-xs ${!activeSubscription ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'} px-2 py-1 rounded-full`}
                              >
                                {processingCheckoutType === 'addon-development-20' ? 'Processing...' : 'Add'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Orders & Subscriptions */}
          {activeTab === "orders" && (
            <>
              {/* Subscriptions List */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium">Your Subscriptions</h2>
                  <p className="text-sm text-gray-500">All your current and past subscriptions</p>
                </div>
                <div className="overflow-hidden">
                  {subscriptions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Plan
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Started
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Ends/Renews
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {subscriptions.map((subscription) => (
                            <tr key={subscription.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {subscription.plan_name || 'Subscription'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  subscription.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {subscription.status === 'active' ? 'Active' : 'Canceled'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(subscription.start_date)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(subscription.current_period_end)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                ${(((subscription.amount || subscription.amount_paid || 0) / 100) || 0).toFixed(2)} / {subscription.recurring_interval || 'period'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions</h3>
                      <p className="mt-1 text-sm text-gray-500">You don't have any subscriptions yet.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Orders List */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                <div 
                  className="px-6 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                  onClick={() => setShowOrderHistory(!showOrderHistory)}
                >
                  <div>
                    <h2 className="text-lg font-medium">Order History</h2>
                    <p className="text-sm text-gray-500">Your past orders and one-time purchases</p>
                  </div>
                  <button className="text-gray-500">
                    {showOrderHistory ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                </div>
                
                {showOrderHistory && (
                  <div className="overflow-hidden">
                    {orders.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Order ID
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Product
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {orders.map((order) => (
                              <tr key={order.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {order.id.substring(0, 8)}...
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {order.product_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatDate(order.created_at)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    {order.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  ${((order.total_amount || 0) / 100).toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                  <button
                                    onClick={() => handleDownloadInvoice(order.id)}
                                    className="text-blue-600 hover:text-blue-900 flex items-center justify-end w-full"
                                    disabled={loading}
                                  >
                                    {loading ? (
                                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                    ) : (
                                      <Download className="h-4 w-4 mr-1" />
                                    )}
                                    Invoice
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No orders</h3>
                        <p className="mt-1 text-sm text-gray-500">You haven't placed any orders yet.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Toast notifications */}
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </div>
  );
};

Settings.propTypes = {
  activeTab: PropTypes.oneOf(["account", "products", "orders"]),
  onSubscriptionUpdate: PropTypes.func
};

export default Settings;